import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface CompanyContext {
  userId: string;
  companyId: string | null;
  companyName: string | null;
  isPremium: boolean;
  jobIds: string[];
}

export const COMPANY_CONTEXT_KEY = "company-context";

/**
 * Eén gedeelde basisbevraging per bedrijf: bedrijf-id, naam, premiumstatus en
 * de vacature-ids. Control Room, To do, kandidaten, zijbalk en detailpagina
 * delen dit resultaat in plaats van elk hun eigen companies/jobs-query te doen.
 */
export const useCompanyContext = () => {
  const { user } = useAuth();

  return useQuery<CompanyContext | null>({
    queryKey: [COMPANY_CONTEXT_KEY, user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      // Eén netwerkronde: bedrijf + vacature-ids samen, zodat de hoofdbevraging
      // niet hoeft te wachten op twee opeenvolgende verzoeken.
      const { data: company } = await supabase
        .from("companies")
        .select("id, name, is_premium, jobs(id)")
        .eq("user_id", user.id)
        .maybeSingle();

      const jobIds = ((company as any)?.jobs || []).map((j: any) => j.id as string);

      return {
        userId: user.id,
        companyId: company?.id ?? null,
        companyName: company?.name ?? null,
        isPremium: !!company?.is_premium,
        jobIds,
      };
    },

    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Na een upgrade/checkout moet de premiumstatus meteen kloppen; de cache van 5
 * minuten zou anders nog het "gratis"-scherm tonen.
 */
export const useInvalidateCompanyContext = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: [COMPANY_CONTEXT_KEY] });
    queryClient.invalidateQueries({ queryKey: ["company-premium-status"] });
    queryClient.invalidateQueries({ queryKey: ["company-subscription"] });
  };
};
